import { useTranslation } from "react-i18next";
import { useEffect, useState } from 'react';
import { ButtonGroup, Col, Dropdown, FormLabel, Row } from 'react-bootstrap';
import FilterAccordion from "../../../Common/Filter/FilterAccordion";
import { errorAlert, successAlert, warnAlert } from "../../../alert/defaultAlert";
import { SearchOperation } from "../../../../../utils/ApiUtils";
import axiosInstance from "../../../../../services/AxiosInstance";
import Api from "../../../../../constants/Api";
import GridCustomBase from "../../../GridCustom/GridCustomBase";
import Common from "../../../../../constants/Common";
import InputText from "../../../Forms/Element/InputText";
import { useNavigate } from "react-router-dom";
import MenuConfig from "../../../../../constants/MenuConfig";
import SaveIcon from "../../../Icon/SaveIcon";
import EditIcon from "../../../Icon/EditIcon";
import TrashIcon from "../../../Icon/TrashIcon";
import YesNoSelect from "../../../Common/YesNoSelect";

const ScoreHistory = () => {

    const { t } = useTranslation();
    const navigate = useNavigate();

    const [pageable, setPageable] = useState(null);
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(Common.DEFAULT_PAGE_SIZE);
    const [rows, setRows] = useState([]);
    const [showLoading, setShowLoading] = useState(true);
    const [selectedRow, setSelectedRow] = useState(null);

    // Filtros
    const [product, setProduct] = useState('');
    const [score, setScore] = useState('');
    const [status, setStatus] = useState(null);

    const search = () => {
        setShowLoading(true);

        // Score não tem branch — o tenant já é filtrado automaticamente pelo backend.
        const filters = [];

        if (product !== '') filters.push({ field: "product.name", value: product, operation: SearchOperation.LIKE });
        if (score !== '') filters.push({ field: "score", value: score, operation: SearchOperation.EQUAL });
        if (status !== null) filters.push({ field: "status", value: status.value, operation: SearchOperation.EQUAL });

        // Endpoint correto é /pageable; o body leva filters + paginação juntos.
        axiosInstance.post(Api.gamification.score + "/pageable", { filters, page, size, orders: [] })
            .then(function (response) {
                setPageable(response.data);
                setRows(response.data.content);
            })
            .catch(function (error) {
                errorAlert(error);
            })
            .finally(function () {
                setShowLoading(false);
            });
    };

    useEffect(() => {
        search();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, size]);

    const handleSearchSubmit = () => {
        setPage(0);
        search();
    };

    const handleClearFilters = () => {
        setProduct('');
        setScore('');
        setStatus(null);
        setPage(0);
    };

    const redirectToNew = () => {
        navigate(`${MenuConfig.gamification.score.route}/new`);
    };

    const redirectToUpdate = () => {
        if (!selectedRow) {
            warnAlert(t('validation.required.row'));
            return;
        }
        navigate(`${MenuConfig.gamification.score.route}/new/${selectedRow.id}`);
    };

    const handleRemove = () => {
        if (!selectedRow) {
            warnAlert(t('validation.required.row'));
            return;
        }
        if (window.confirm(t('message.remove_the_selected_item'))) {
            axiosInstance.delete(`${Api.gamification.score}/${selectedRow.id}`)
                .then(function () {
                    successAlert(t('message.removed_successfully'));
                    setSelectedRow(null);
                    search();
                })
                .catch(function (error) {
                    errorAlert(error);
                });
        }
    };

    return (
        <>
            <FilterAccordion
                onSearch={handleSearchSubmit}
                onClear={handleClearFilters}
            >
                <Row>
                    <Col md={5}>
                        <FormLabel>{t('product')}</FormLabel>
                        <InputText
                            value={product}
                            onChange={(e) => setProduct(e.target.value)}
                            placeholder={t('search')}
                        />
                    </Col>
                    <Col md={3}>
                        <FormLabel>{t('gamification.score')}</FormLabel>
                        <InputText
                            value={score}
                            onChange={(e) => setScore(e.target.value)}
                            placeholder={t('search')}
                        />
                    </Col>
                    <Col md={4}>
                        <FormLabel>{t('status')}</FormLabel>
                        <YesNoSelect
                            value={status}
                            onChange={(value) => setStatus(value)}
                            isClearable={true}
                        />
                    </Col>
                </Row>
            </FilterAccordion>

            <Row className="row mb-3 mt-4">
                <Col md={12}>
                    <ButtonGroup>
                        <Dropdown>
                            <Dropdown.Toggle className="me-2" variant="primary">
                                {t('actions')}
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                                <Dropdown.Item onClick={() => redirectToNew()}>
                                    <SaveIcon /> {t('new')}
                                </Dropdown.Item>
                                <Dropdown.Item onClick={() => redirectToUpdate()}>
                                    <EditIcon /> {t('update')}
                                </Dropdown.Item>
                                <Dropdown.Item onClick={() => handleRemove()}>
                                    <TrashIcon /> {t('remove')}
                                </Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>
                    </ButtonGroup>
                </Col>
            </Row>

            <GridCustomBase
                pageable={pageable}
                setPage={setPage}
                setSize={setSize}
                showLoading={showLoading}>

                <table className="table table-list i-table style-1 mb-0 border-0">
                    <thead>
                        <tr>
                            <th style={{ width: 400 }}>{t("product")}</th>
                            <th style={{ width: 150 }} className="text-center">{t("gamification.score")}</th>
                            <th style={{ width: 120 }} className="text-center">{t("status")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {/* Destaque da grade comum (GridCustomBase.css): o .table-active do Bootstrap
                            deixava a linha branca no tema da Colmeia. */}
                        {rows.map((row, index) => (
                            <tr
                                key={index}
                                className={`align-middle grid-custom__row--selectable${selectedRow?.id === row.id ? ' grid-custom__row--selected' : ''}`}
                                onClick={() => setSelectedRow(selectedRow?.id === row.id ? null : row)}
                                aria-selected={selectedRow?.id === row.id}
                            >
                                <td>
                                    <div className="d-flex align-items-center">
                                        <i className="fas fa-box text-primary me-2 fs-5"></i>
                                        <span className="fw-semibold">{row.product.name}</span>
                                    </div>
                                </td>
                                <td className="text-center">
                                    {row.score}
                                </td>
                                <td className="text-center">
                                    {t(row.status.toLowerCase())}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </GridCustomBase>
        </>
    );
}

export default ScoreHistory;
